import Graphic from "@arcgis/core/Graphic";
import GraphicsLayer from "@arcgis/core/layers/GraphicsLayer";
import Map from "@arcgis/core/Map";
import MapView from "@arcgis/core/views/MapView";
import DeleteIcon from "@mui/icons-material/Delete";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import PlaceOutlinedIcon from "@mui/icons-material/PlaceOutlined";
import PolylineIcon from "@mui/icons-material/Polyline";
import RectangleOutlinedIcon from "@mui/icons-material/RectangleOutlined";
import {
  alpha,
  Divider,
  IconButton,
  ListItem,
  ListItemText,
  Menu,
  MenuItem,
  MenuProps,
  styled,
} from "@mui/material";
import React, { useCallback, useEffect, useState } from "react";
import { FixedSizeList as List } from "react-window";
import { AddStyle } from "./DrawStyles.helper";
import styles from "./SpatialAnalysis.module.css";
interface GraphicsListProps {
  map?: Map;
  view?: MapView;
  height: number;
}

type Type = "polygon" | "polyline" | "point" | "multipoint" | "extent" | "mesh";

export const GraphicsList: React.FC<GraphicsListProps> = ({ map, view, height }) => {
  const [items, setItems] = useState<Graphic[]>([]);
  const [graphicsLayer, setGraphicsLayer] = useState<GraphicsLayer>();
  const [hoveredIndex, setHoveredIndex] = useState<number | string>("");

  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);

  useEffect(() => {
    if (!map) return;
    const gl = map.findLayerById("draw");
    if (gl instanceof GraphicsLayer) {
      setGraphicsLayer(gl);

      const handle = gl.graphics.on("change", () => {
        if (gl.graphics.length === 0) {
          setItems([]);
        }
      });

      return () => {
        handle.remove();
      };
    }
  }, [map, view]);

  useEffect(() => {
    if (!graphicsLayer) return;
    graphicsLayer.graphics.on("change", updateGraphics);
  }, [graphicsLayer]);

  const updateGraphics = useCallback(() => {
    if (!graphicsLayer) return;
    const currentGraphics = graphicsLayer.graphics.toArray();
    if (currentGraphics.length !== items.length) {
      setItems(currentGraphics);
    }
  }, [graphicsLayer, items.length]);

  const renderTypeIcon = (type: Type) => {
    switch (type) {
      case "point":
        return <PlaceOutlinedIcon />;
      case "polyline":
        return <PolylineIcon />;
      case "polygon":
        return <RectangleOutlinedIcon />;
      default:
        return <PlaceOutlinedIcon />;
    }
  };

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    event.preventDefault();
    setAnchorEl(event.currentTarget);
  };
  const handleClose = () => {
    setAnchorEl(null);
  };
  const handleRemove = (index: number) => {
    console.log(index);
    graphicsLayer?.remove(items[index]);
    setAnchorEl(null);
  };

  const contextMenu = (index: number) => {
    const StyledMenu = styled((props: MenuProps) => (
      <Menu elevation={0} {...props} />
    ))(({ theme }) => ({
      "& .MuiPaper-root": {
        borderRadius: 6,
        marginTop: theme.spacing(1),
        minWidth: 180,
        color: "rgb(55, 65, 81)",
        boxShadow:
          "rgb(255, 255, 255) 0px 0px 0px 0px, rgba(0, 0, 0, 0.05) 0px 0px 0px 1px, rgba(0, 0, 0, 0.1) 0px 10px 15px -3px, rgba(0, 0, 0, 0.05) 0px 4px 6px -2px",
        "& .MuiMenu-list": {
          padding: "4px 0",
        },
        "& .MuiMenuItem-root": {
          "& .MuiSvgIcon-root": {
            fontSize: 18,
            color: theme.palette.text.secondary,
            marginRight: theme.spacing(1.5),
          },
          "&:active": {
            backgroundColor: alpha(
              theme.palette.primary.main,
              theme.palette.action.selectedOpacity
            ),
          },
        },
        ...theme.applyStyles("dark", {
          color: theme.palette.grey[300],
        }),
      },
    }));
    return (
      <StyledMenu
        id="demo-customized-menu"
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleClose}
      >
        <MenuItem onClick={handleClose} disableRipple>
          <MoreVertIcon />
          Duplicate
        </MenuItem>
        <Divider sx={{ my: 0.5 }} />
        <MenuItem onClick={() => handleRemove(index)} disableRipple>
          <DeleteIcon />
          Delete
        </MenuItem>
      </StyledMenu>
    );
  };

  const handleHover = useCallback((index: number, graphic: Graphic) => {
    setHoveredIndex(index);

    if (!graphic) return;
    graphic.symbol = AddStyle(graphic, "highligh");
  }, []);

  const handleMouseLeave = useCallback((graphic: Graphic) => {
    setHoveredIndex("");

    if (!graphic) return;
    graphic.symbol = AddStyle(graphic, "sketch");
  }, []);

  const Row = ({
    index,
    style,
  }: {
    index: number;
    style: React.CSSProperties;
  }) => {
    const graphic = items[index];

    return (
      <div style={style} key={index}>
        <ListItem
          className={`${
            hoveredIndex === index
              ? styles.hoverTarget
              : styles.displayListElement
          }`}
          onMouseEnter={() => handleHover(index, graphic)}
          onMouseLeave={() => handleMouseLeave(graphic)}
        >
          <IconButton
            title="Zoom"
            onClick={() => {
              view?.goTo({ target: graphic.geometry, zoom: 18 });
            }}
          >
            {renderTypeIcon(graphic.geometry.type)}
          </IconButton>

          <ListItemText
            onClick={() => {}}
            primary={graphic?.attributes?.name ?? "Geometry"}
            /* secondary="Secondary text" */
          />

          {hoveredIndex === index && (
            <>
              <IconButton
                key={index}
                edge="end"
                aria-label="delete"
                tabIndex={index}
                onClick={handleClick}
              >
                <MoreVertIcon />
              </IconButton>
              {contextMenu(index)}
            </>
          )}
        </ListItem>
      </div>
    );
  };
  return (
    <>
      <List height={height} itemCount={items.length} itemSize={56} width="100%">
        {Row}
      </List>
    </>
  );
};

export default GraphicsList;
